import React from "react";
import {Input, InputNumber, Popconfirm, Tooltip} from "antd";
import {DeleteOutlined, PlusOutlined, InfoCircleOutlined} from "@ant-design/icons";
import {PoolCustomTimeFeeItem} from "./PoolCustomTimeFeeItem";
import config from "_core/config";
import {
    FeeTypeCard as StyledFeeTypeCard,
    FeeTypeHeader,
    FeeTypeTitle,
    FeeTypeBody,
    FormItem,
    FormLabel,
    FormInputRow,
    CustomTimeSection,
    CustomTimeHeader,
    CustomTimeTitle,
    CustomTimeList,
    AddButton
} from "../PoolPaymentContractForm.styles";

const KDV_RATE = config.KDV;

// KDV tooltip içeriği oluşturma fonksiyonu
const getKdvTooltipContent = (amount, isClientAmount) => {
    if (!amount || amount === "" || parseFloat(amount) === 0) return null;

    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount)) return null;

    // Müşteri ücreti KDV hariç, Kurye ücreti KDV dahil
    let includeVAT, excludeVAT;
    if (isClientAmount) {
        // Müşteri: girilen değer KDV hariç
        excludeVAT = numericAmount;
        includeVAT = numericAmount * (1 + KDV_RATE);
    } else {
        // Kurye: girilen değer KDV dahil
        includeVAT = numericAmount;
        excludeVAT = numericAmount / (1 + KDV_RATE);
    }

    return (
        <div style={{fontSize: 12}}>
            <div>KDV Dahil: {includeVAT.toFixed(2)} ₺</div>
            <div>KDV Hariç: {excludeVAT.toFixed(2)} ₺</div>
            <div style={{marginTop: 4, color: "#faad14"}}>KDV: %{(KDV_RATE * 100).toFixed(0)}</div>
        </div>
    );
};

export const PoolFeeTypeCard = ({
    feeType,
    feeTypeName,
    defaultFeeItem,
    customFeeItems,
    disabled,
    onDefaultChange,
    onCustomItemChange,
    onAddCustomItem,
    onRemoveCustomItem,
    onRemoveFeeType,
    userIsCustomer
}) => {
    const handleDefaultFieldChange = (field, value) => {
        onDefaultChange(feeType, field, value);
    };

    const handleCustomItemChange = (index, field, value) => {
        onCustomItemChange(feeType, index, field, value);
    };

    const handleRemoveCustomItem = index => {
        onRemoveCustomItem(feeType, index);
    };

    const handleAddCustomItem = () => {
        onAddCustomItem(feeType);
    };

    const needsCourierMinimumDistance = feeType === "KM";
    const needsClientMinimumDistance = feeType === "KM";
    const showCustomTimeSection = defaultFeeItem !== undefined;

    return (
        <StyledFeeTypeCard>
            <FeeTypeHeader>
                <FeeTypeTitle>{feeTypeName}</FeeTypeTitle>
                {!disabled && onRemoveFeeType && (
                    <Popconfirm
                        title="Bu ücret tipini kaldırmak istediğinize emin misiniz?"
                        onConfirm={() => onRemoveFeeType(feeType)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <DeleteOutlined
                            style={{
                                fontSize: 16,
                                color: "#ff4d4f",
                                cursor: "pointer",
                                transition: "all 0.3s"
                            }}
                            onMouseEnter={e => (e.currentTarget.style.color = "#ff7875")}
                            onMouseLeave={e => (e.currentTarget.style.color = "#ff4d4f")}
                        />
                    </Popconfirm>
                )}
            </FeeTypeHeader>

            <FeeTypeBody>
                <FormInputRow style={{flexWrap: "wrap", gap: 8}}>
                    <FormItem style={{minWidth: 90}}>
                        <FormLabel style={{display: "flex", alignItems: "center", gap: 4}}>
                            {userIsCustomer ? "Ücret" : "Müşteri Ücreti"}
                            {defaultFeeItem?.clientAmount ? (
                                getKdvTooltipContent(defaultFeeItem?.clientAmount, true) && (
                                    <Tooltip
                                        title={getKdvTooltipContent(
                                            defaultFeeItem?.clientAmount,
                                            true
                                        )}
                                    >
                                        <InfoCircleOutlined style={{color: "#1890ff"}} />
                                    </Tooltip>
                                )
                            ) : (
                                <InfoCircleOutlined />
                            )}
                        </FormLabel>
                        <InputNumber
                            className="payment-contract-form-input"
                            type="number"
                            size="small"
                            value={defaultFeeItem?.clientAmount ?? ""}
                            onChange={e => handleDefaultFieldChange("clientAmount", Math.abs(e))}
                            disabled={disabled}
                            min={0}
                        />
                    </FormItem>

                    {!userIsCustomer && (
                        <FormItem style={{minWidth: 90}}>
                            <FormLabel style={{display: "flex", alignItems: "center", gap: 4}}>
                                Kurye Ücreti
                                {defaultFeeItem?.courierAmount ? (
                                    getKdvTooltipContent(defaultFeeItem?.courierAmount, false) && (
                                        <Tooltip
                                            title={getKdvTooltipContent(
                                                defaultFeeItem?.courierAmount,
                                                false
                                            )}
                                        >
                                            <InfoCircleOutlined style={{color: "#1890ff"}} />
                                        </Tooltip>
                                    )
                                ) : (
                                    <InfoCircleOutlined />
                                )}
                            </FormLabel>
                            <InputNumber
                                className="payment-contract-form-input"
                                type="number"
                                size="small"
                                value={defaultFeeItem?.courierAmount ?? ""}
                                onChange={e =>
                                    handleDefaultFieldChange("courierAmount", Math.abs(e))
                                }
                                disabled={disabled}
                                min={0}
                            />
                        </FormItem>
                    )}

                    {!userIsCustomer && needsCourierMinimumDistance && (
                        <FormItem style={{minWidth: 110}}>
                            <FormLabel>Kurye Minimum Mesafe (km)</FormLabel>
                            <InputNumber
                                className="payment-contract-form-input"
                                type="number"
                                size="small"
                                value={defaultFeeItem?.courierMinimumDistance ?? ""}
                                onChange={e =>
                                    handleDefaultFieldChange("courierMinimumDistance", Math.abs(e))
                                }
                                disabled={disabled}
                                min={0}
                            />
                        </FormItem>
                    )}

                    {needsClientMinimumDistance && (
                        <FormItem style={{minWidth: 110}}>
                            <FormLabel>Müşteri Minimum Mesafe (km)</FormLabel>
                            <InputNumber
                                className="payment-contract-form-input"
                                type="number"
                                size="small"
                                value={defaultFeeItem?.clientMinimumDistance ?? ""}
                                onChange={e =>
                                    handleDefaultFieldChange("clientMinimumDistance", Math.abs(e))
                                }
                                disabled={disabled}
                                min={0}
                            />
                        </FormItem>
                    )}
                </FormInputRow>

                {showCustomTimeSection && (
                    <CustomTimeSection>
                        <CustomTimeHeader>
                            <CustomTimeTitle>Özel Gün Tanımlamaları</CustomTimeTitle>
                        </CustomTimeHeader>

                        <CustomTimeList>
                            {customFeeItems?.map((feeItem, index) => (
                                <PoolCustomTimeFeeItem
                                    key={index}
                                    feeItem={feeItem}
                                    feeType={feeType}
                                    index={index}
                                    disabled={disabled}
                                    onRemove={handleRemoveCustomItem}
                                    onChange={handleCustomItemChange}
                                    userIsCustomer={userIsCustomer}
                                />
                            ))}
                        </CustomTimeList>

                        {!disabled && (
                            <AddButton onClick={handleAddCustomItem}>
                                <PlusOutlined />
                                Özel Gün Ekle
                            </AddButton>
                        )}
                    </CustomTimeSection>
                )}
            </FeeTypeBody>
        </StyledFeeTypeCard>
    );
};
